/**
 * Directory follow — when the tab re-fetches the core directory
 * (directory-follow, 2026-10-14).
 *
 * The directory used to be fetched once per page load, so a tab kept the
 * pair assignments it booted with for its whole life: after hc1's staged
 * transfer NBA he3 → heklpi-nbamain-f01 (2026-10-08) every open tab still
 * named he3 and sent its NBA books to the floor. Now: the boot fetch, plus
 *
 *   - a trigger from the store (an edge added or removed a pair claim; a
 *     named primary's or sole claimant's connection moved),
 *   - a slow period: `DIRECTORY_FOLLOW_PERIOD_MS` after the last good fetch.
 *
 * Triggers inside one `DIRECTORY_FOLLOW_COALESCE_MS` window are one fetch; a
 * trigger while a fetch is in flight schedules one follow-up, never a pile.
 * A failed fetch retries on the board's index ladder (5 s, 15 s, 30 s, then
 * 60 s); triggers during that wait ride the pending retry instead of
 * hammering an unreachable relay. Hidden tabs fetch too — the answer only
 * moves the store's assignments, and routing keeps its own hidden-tab rules.
 *
 * One short re-check: hc1 flips the directory only when a transfer
 * COMPLETEs, which is after the donor has gone silent, so the fetch a
 * donor's silence triggers can still name the donor. While the store reports
 * a named primary that abandoned a pair and is still named (`awaiting`
 * non-zero), a good answer re-checks at 5 s, 15 s and 30 s
 * (`DIRECTORY_FOLLOW_SETTLE_MS`) before falling back to the period. That is
 * at most three fetches per abandonment: a new abandonment (a new `awaiting`
 * value) restarts the ladder, and an unchanged one never does. The books do
 * not wait on it — the sole announcing claimant owns the pair meanwhile, or
 * the floor does — but the tab learns the new primary within seconds.
 *
 * This module only decides WHEN. `refresh` is `refreshFeedDirectory()`, whose
 * request-correlation and lineage/generation guards stay the one adoption
 * path; a failure there is a non-event for the fleet (rule 6) and only moves
 * this schedule onto the retry ladder.
 */
import { INDEX_REFRESH_MS, INDEX_RETRY_MS } from './edgeIndexCycle';

/** The board's re-index cadence; never faster. */
export const DIRECTORY_FOLLOW_PERIOD_MS = INDEX_REFRESH_MS;
export const DIRECTORY_FOLLOW_COALESCE_MS = 1_000;
export const DIRECTORY_FOLLOW_RETRY_MS = INDEX_RETRY_MS;
/** Re-checks while a named primary's abandoned pair waits on the directory. */
export const DIRECTORY_FOLLOW_SETTLE_MS = [5_000, 15_000, 30_000] as const;

export interface DirectoryFollower {
  /** Begin: fetch now when `boot`, else wait one period. */
  start(boot: boolean): void;
  /** Something moved that may mean the directory did too. */
  trigger(): void;
  stop(): void;
}

/** `refresh` resolves true when a core answered (adopted or not), false when
 *  none did. `awaiting` is the store's `getDirectoryAwaitEpoch`: 0 when no
 *  abandoned pair waits on the directory. */
export function createDirectoryFollower(
  refresh: () => Promise<boolean>, awaiting: () => number = () => 0,
): DirectoryFollower {
  type TimerKind = 'coalesce' | 'period' | 'retry' | 'settle';
  let timer: ReturnType<typeof setTimeout> | null = null;
  let timerKind: TimerKind | null = null;
  let running = false;
  let inFlight = false;
  let followUp = false;
  let failures = 0;
  // The abandonment the settle ladder is running for, and its next step.
  let settleFor = 0;
  let settleStep = 0;
  // A stop() → start() while a fetch is in flight must not let the old
  // fetch's completion arm a second schedule.
  let epoch = 0;

  const clear = () => {
    if (timer != null) clearTimeout(timer);
    timer = null;
    timerKind = null;
  };
  const arm = (kind: TimerKind, ms: number) => {
    clear();
    timerKind = kind;
    timer = setTimeout(() => {
      timer = null;
      timerKind = null;
      void run();
    }, ms);
  };
  const run = async () => {
    if (!running) return;
    const mine = epoch;
    inFlight = true;
    followUp = false;
    let ok = false;
    try {
      ok = await refresh();
    } catch {
      ok = false;
    }
    if (mine !== epoch) return;
    inFlight = false;
    if (!running) return;
    if (!ok) {
      arm('retry', DIRECTORY_FOLLOW_RETRY_MS[Math.min(failures, DIRECTORY_FOLLOW_RETRY_MS.length - 1)]);
      failures += 1;
      return;
    }
    failures = 0;
    if (followUp) {
      arm('coalesce', DIRECTORY_FOLLOW_COALESCE_MS);
      return;
    }
    const waiting = awaiting();
    if (waiting !== settleFor) {
      settleFor = waiting;
      settleStep = 0;
    }
    if (waiting !== 0 && settleStep < DIRECTORY_FOLLOW_SETTLE_MS.length) {
      arm('settle', DIRECTORY_FOLLOW_SETTLE_MS[settleStep++]);
    } else {
      arm('period', DIRECTORY_FOLLOW_PERIOD_MS);
    }
  };

  return {
    start(boot) {
      if (running) return;
      running = true;
      if (boot) void run();
      else arm('period', DIRECTORY_FOLLOW_PERIOD_MS);
    },
    trigger() {
      if (!running) return;
      if (inFlight) {
        followUp = true;
        return;
      }
      // Already due: a pending coalesced fetch or a retry covers this one.
      if (timerKind === 'coalesce' || timerKind === 'retry') return;
      arm('coalesce', DIRECTORY_FOLLOW_COALESCE_MS);
    },
    stop() {
      epoch += 1;
      running = false;
      inFlight = false;
      followUp = false;
      failures = 0;
      settleFor = 0;
      settleStep = 0;
      clear();
    },
  };
}
